import axios from 'axios';
import { Message } from '@arco-design/web-react';
import { getToken, useAuthStore } from './store/auth';
import { useNetStore } from './store/net';
import type { NetDownReason } from './store/net';

// 后端统一响应：{ code: 0, msg: "ok", data: ... }
// 分页响应：data = { list: [...], total: n }

export interface ReqConfig {
  params?: any;
  silent?: boolean; // 出错时不弹全局 Message
  timeout?: number; // 覆盖默认超时（如文件上传）
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer'; // 下载类请求（如 xlsx/csv 模板）
  headers?: Record<string, string>; // 覆盖请求头（2FA 绑定阶段用 step_token 作 Bearer）
}

const instance = axios.create({
  baseURL: '/api/v1',
  timeout: 20000,
});

/** 会话过期提示的落地键：401 会强制整页跳登录，Toast 活不过跳转，故由登录页读取后提示 */
export const AUTH_EXPIRED_KEY = 'bcc.auth.expired';

/** 被顶下线提示的落地键（坐席账号在别处登录）：与「登录过期」分开，登录页据此换文案 */
export const AUTH_KICKED_KEY = 'bcc.auth.kicked';

/** 后端「已被新登录顶下线」业务码（HTTP 401 + code=4011，见 server/internal/module/system/session.go） */
const CODE_SESSION_KICKED = 4011;

/**
 * 传输层/网关层错误的中文兜底（后端业务错误自带中文 `msg`，这里只兜"没有 msg"的场景）。
 * 不兜底时用户看到的是 axios 原文（`Network Error`、`timeout of 20000ms exceeded`、
 * `Request failed with status code 502`）——既读不懂，也分不清是自己网络问题还是服务故障。
 */
function fallbackMsg(status?: number, code?: string): string {
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return '请求超时，请稍后重试';
  if (code === 'ERR_NETWORK' || status === 0) return '网络连接失败，请检查网络后重试';
  if (status === 400) return '请求参数有误，请检查后重试';
  if (status === 403) return '没有权限执行该操作';
  if (status === 404) return '接口不存在或已被移除';
  if (status === 413) return '内容过大，请缩小后重试';
  if (status === 502 || status === 503 || status === 504) return '服务暂时不可用，请稍后重试';
  if (status && status >= 500) return '服务异常，请稍后重试或联系管理员';
  return '请求失败，请稍后重试';
}

/**
 * 失败风暴判据：1 秒内出现 3 次以上失败 = 页面级成片失败（用户操作不可能这么密），
 * 升级为全局不可用提示（一条 Message，文案换成「多个请求同时失败」），
 * 并且这一轮不再逐条弹 toast。用滚动窗口而不是固定静默期：失败停下来后提示能力自然恢复。
 */
const BURST_WINDOW_MS = 1000;
const BURST_MIN_FAILURES = 3;
let recentFailures: number[] = [];

/** 记一次失败；返回 true 表示本次（及同轮后续）失败都不该弹 toast */
function inFailureBurst(): boolean {
  const now = Date.now();
  recentFailures = recentFailures.filter((t) => now - t < BURST_WINDOW_MS);
  recentFailures.push(now);
  if (recentFailures.length >= BURST_MIN_FAILURES) {
    notifyServiceDown('degraded');
    return true;
  }
  return false;
}

/**
 * 失败提示的唯一出口（去重后交 Message）。
 *
 * 两条降噪口径：
 * 1. **同一句文案在窗口期内只弹一次**——同一次刷新里的十几个请求往往是同一个原因，
 *    逐条弹就是刷屏；不同文案（不同接口/不同原因）各弹一次，信息不丢。
 * 2. **延迟 200ms 再弹**——成片的失败都挤在同一瞬间，留这个空隙让风暴判据先判定：
 *    真成片就把待弹的这条撤掉、只留一条全局不可用提示；单条业务错误 200ms 后照常出现，用户无感。
 */
const ERROR_TOAST_WINDOW_MS = 3000;
const TOAST_DELAY_MS = 200;
const lastToastAt = new Map<string, number>();
let pendingToast: { msg: string; timer: number } | null = null;

/** 撤掉还没弹出去的那条失败提示（已被成片失败 / 中断提示顶掉） */
function cancelPendingToast() {
  if (!pendingToast) return;
  clearTimeout(pendingToast.timer);
  pendingToast = null;
}

function notifyFailure(msg: string) {
  if (inFailureBurst()) {
    cancelPendingToast();
    return;
  }
  const now = Date.now();
  // 顺手清理过期键，避免 Map 随文案种类无上限增长
  for (const [k, t] of lastToastAt) {
    if (now - t > ERROR_TOAST_WINDOW_MS) lastToastAt.delete(k);
  }
  if (now - (lastToastAt.get(msg) || 0) < ERROR_TOAST_WINDOW_MS) return;
  if (pendingToast) clearTimeout(pendingToast.timer);
  pendingToast = {
    msg,
    timer: window.setTimeout(() => {
      pendingToast = null;
      lastToastAt.set(msg, Date.now());
      Message.error(msg);
    }, TOAST_DELAY_MS),
  };
}

/**
 * 服务不可用提示文案：请求没到后端（网络不可达/超时/网关不可用）与页面级成片失败分开说，
 * 用户据此能分清该检查自己的网络还是等服务端恢复。
 */
const DOWN_MSG: Record<'unreachable' | 'degraded', string> = {
  unreachable: '服务暂时不可用：请求没有到达后端，请检查网络后重试。',
  degraded: '服务异常：多个请求同时失败，请稍后重试。',
};

/** 不可用提示的停留时长：比普通失败提示长一些，够用户读完并检查网络 */
const DOWN_TOAST_DURATION_MS = 5000;

/**
 * 不可用提示的最小间隔（**按提示种类各算一份**）：同一句提示 15 秒内只弹一条。
 * 它兜住两种抖动——状态被后续请求反复复位、成片失败一轮接一轮——保证任何情况下都不刷屏；
 * 两种原因互不顶替，所以「刚提示过没到后端，随即又成片失败」仍然给得出提示。
 */
const DOWN_NOTICE_MIN_GAP_MS = 15000;
const lastDownNoticeAt: Record<'unreachable' | 'degraded', number> = { unreachable: 0, degraded: 0 };

/**
 * 全局不可用提示：**只用 Message，不做页内元素**——页内横幅占布局空间，
 * 它的出现与消失会把内容整体推移、页面高度跟着变。
 *
 * 节流：置位那一刻弹一条，同一句提示至少间隔 `DOWN_NOTICE_MIN_GAP_MS` 才再提醒一条
 * （中断拖着不停时相当于周期性提醒）；单条失败的普通提示不受它影响（见 `notifyFailure`）。
 */
function notifyServiceDown(reason: NetDownReason) {
  const { down, setDown } = useNetStore.getState();
  if (!reason) {
    if (down) setDown(null);
    return;
  }
  // 不降级：请求根本没到后端时，「没到后端」比「多个请求失败」更准确
  if (down === 'unreachable' && reason === 'degraded') return;
  setDown(reason);
  const now = Date.now();
  if (now - lastDownNoticeAt[reason] < DOWN_NOTICE_MIN_GAP_MS) return;
  lastDownNoticeAt[reason] = now;
  // 同一次失败不再叠加单条提示：一条中断提示足够（成片失败那条同样被它顶掉）
  cancelPendingToast();
  Message.error({ content: DOWN_MSG[reason], duration: DOWN_TOAST_DURATION_MS });
}

/**
 * 服务不可用判据：网络不可达 / 超时 / 网关与服务端不可用。
 * 这类失败一次会成片出现且文案相同，提示由 `notifyServiceDown` 收口（不逐条弹）。
 */
function isServiceDown(status?: number, code?: string): boolean {
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || code === 'ERR_NETWORK' || status === 0) return true;
  return status === 502 || status === 503 || status === 504;
}

/**
 * 「整个服务不可用」判据：1 秒内有**两个不同接口**都没到后端。
 * 只有一个接口没到后端（后端其实可达，例如某个查询一直失败）不算——那时只提示那一条失败本身，
 * 否则一次局部故障会冒充「全站不可用」。
 */
const OUTAGE_WINDOW_MS = 1000;
let recentTransportFailures: { at: number; path: string }[] = [];

function isServiceWideOutage(url: string): boolean {
  const now = Date.now();
  recentTransportFailures = recentTransportFailures.filter((f) => now - f.at < OUTAGE_WINDOW_MS);
  recentTransportFailures.push({ at: now, path: String(url).split('?')[0] });
  return new Set(recentTransportFailures.map((f) => f.path)).size >= 2;
}

instance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

instance.interceptors.response.use(
  (res) => {
    const cfg = res.config as any;
    const body = res.data;
    // 请求能到达并拿到响应 = 服务可达，复位全局不可用态（提示节流不受影响，见 notifyServiceDown）
    notifyServiceDown(null);
    if (body && typeof body === 'object' && 'code' in body && 'msg' in body) {
      if (body.code === 0) {
        return body.data;
      }
      // W5-4：首次登录未改密，除改密外 API 一律 code=4001 → 全局强制改密 Modal。
      // 该错误的说明由 Modal 承载，不再叠加 Toast：页面加载会并发多个请求，逐个弹会刷屏。
      if (body.code === 4001) {
        useAuthStore.getState().openPwdModal(true);
      } else if (!cfg.silent) {
        notifyFailure(body.msg || '请求失败');
      }
      const bizErr = new Error(body.msg || 'request failed') as Error & { msg?: string };
      // 归一化：catch 里 e.msg 恒可读（后端 msg / 兜底文案），避免页面永远取空回退成「操作失败」类噪音
      bizErr.msg = body.msg || bizErr.message;
      return Promise.reject(bizErr);
    }
    // 二进制响应（音频/文件下载）：把响应头挂到 Blob 上——服务端实测指标（如 TTS 首音时延）
    // 只能经响应头回传，而拦截器会丢弃 axios 响应对象；这里只挂引用，不改变返回类型。
    if (body instanceof Blob && res.headers) {
      (body as Blob & { headers?: unknown }).headers = res.headers;
    }
    return body;
  },
  (err) => {
    const cfg = (err.config || {}) as any;
    const status = err.response?.status;
    const data = err.response?.data;
    const code = (err as { code?: string }).code;
    const msg = data?.msg || fallbackMsg(status, code);
    // 请求没到后端（网络/超时/网关）→ 先记账，确认是「整个服务不可用」才给全局提示；
    // 请求到了（哪怕回错误码）→ 视为可达而复位（不复位提示节流，避免成片错误把提示反复重置成「可以再弹」）
    if (isServiceDown(status, code)) {
      if (isServiceWideOutage(String(cfg.url || ''))) notifyServiceDown('unreachable');
    } else {
      notifyServiceDown(null);
    }
    if (status === 401) {
      // step-up（重置 2FA / 解锁）二次校验失败：会话仍有效，不得整页登出。
      // 识别路径（disable-2fa/unlock）或「管理员校验失败」文案——后端应回 403，此处兼容仍回 401 的情况。
      const url = String(cfg.url || '');
      const stepupFail =
        /\/users\/\d+\/(disable-2fa|unlock)(?:\?|$)/.test(url) ||
        (typeof msg === 'string' && msg.includes('管理员校验失败'));
      if (stepupFail) {
        if (!cfg.silent) notifyFailure(msg);
      } else {
        useAuthStore.getState().logout();
        const here = window.location.pathname + window.location.search;
        if (window.location.pathname !== '/login') {
          // 提示与回跳一起交给登录页：401 是整页跳转，此刻弹的 Toast 会被跳转吞掉
          try {
            // 被顶下线（坐席在别处登录）与登录过期是两种事，分开留标记由登录页给不同文案
            sessionStorage.setItem(data?.code === CODE_SESSION_KICKED ? AUTH_KICKED_KEY : AUTH_EXPIRED_KEY, '1');
          } catch {
            /* 隐私模式等存储不可用：仅少一条提示，不阻断跳转 */
          }
          window.location.href = `/login?redirect=${encodeURIComponent(here)}`;
        }
      }
    } else if (data?.code === 4001) {
      // W5-4：HTTP 403 + code=4001（未改密）→ 全局强制改密 Modal；
      // 提示已由 Modal 承载，跳过通用 Toast，避免并发请求刷屏
      useAuthStore.getState().openPwdModal(true);
    } else if (!cfg.silent) {
      // 不因为「已判定不可用」就吞掉单条失败：一次网络抖动也要让用户看到原因。
      // 成片失败的降噪交给 notifyFailure 的风暴判据（每秒 ≥3 次时只留一条全局不可用提示）
      notifyFailure(msg);
    }
    // 归一化：给 axios 错误补 e.msg（后端 msg / 兜底文案），.message/.response 原样保留
    // （login 等依赖 err.response.status 的分支不受影响）
    try {
      (err as Error & { msg?: string }).msg = msg;
    } catch { /* 只读对象极端场景忽略 */ }
    return Promise.reject(err);
  }
);

export function get<T = any>(url: string, params?: any, cfg?: ReqConfig): Promise<T> {
  return instance.get(url, { params, ...(cfg || {}) }) as unknown as Promise<T>;
}

export function post<T = any>(url: string, data?: any, cfg?: ReqConfig): Promise<T> {
  return instance.post(url, data, { ...(cfg || {}) }) as unknown as Promise<T>;
}

export function put<T = any>(url: string, data?: any, cfg?: ReqConfig): Promise<T> {
  return instance.put(url, data, { ...(cfg || {}) }) as unknown as Promise<T>;
}

export function patch<T = any>(url: string, data?: any, cfg?: ReqConfig): Promise<T> {
  return instance.patch(url, data, { ...(cfg || {}) }) as unknown as Promise<T>;
}

export function del<T = any>(url: string, cfg?: ReqConfig): Promise<T> {
  return instance.delete(url, { ...(cfg || {}) }) as unknown as Promise<T>;
}

export default instance;
